import { useEffect, useRef, useState } from 'react';
import type { LogoKind } from '../../core/logo';
import {
  DEFAULT_SETTINGS,
  logoUrl,
  saveSettings,
  uploadLogo,
  watchSettings,
  type AppSettings,
} from '../../data/settings';
import { LogoError, processLogo, type ProcessedLogo } from '../branding/processLogo';

/**
 * Branding (FR-SET-02, FR-SET-03a, FR-SET-04).
 *
 * The preview is the point. FR-SET-02 requires the processed result be shown against a
 * sample header, so what you approve is what prints — a logo that looks fine on a white
 * settings page can still carry a grey box into a PDF.
 */
export function BrandingScreen() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  useEffect(() => watchSettings(setSettings), []);

  return (
    <>
      <h1>Branding</h1>
      <p className="muted">
        Used by the two PDF report templates. Personal is chosen automatically for
        personal reports and business for business ones, overridable at export.
      </p>

      <LogoPanel
        kind="personal"
        title="Personal"
        storedPath={settings.personalLogoPath}
        note="Photographs are cropped square and left otherwise untouched — background removal on a photo gives ragged edges, not a cutout."
        header={{ name: settings.personalName, lines: ['Expense report · FY 2026-27'] }}
      />

      <LogoPanel
        kind="business"
        title="Business"
        storedPath={settings.businessLogoPath}
        note="Marks keep their aspect ratio and have their background removed, so they do not print inside a grey box."
        header={{
          name: settings.businessName,
          lines: settings.businessAddress.split(',').reduce<string[]>((acc, part, i) => {
            const t = part.trim();
            if (i % 2 === 0) acc.push(t);
            else acc[acc.length - 1] += `, ${t}`;
            return acc;
          }, []),
        }}
      />

      <section className="panel">
        <h2>Business identity</h2>
        <label className="field">
          <span>Business name</span>
          <input
            defaultValue={settings.businessName}
            onBlur={(e) => void saveSettings({ businessName: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Address</span>
          <textarea
            rows={3}
            defaultValue={settings.businessAddress}
            onBlur={(e) => void saveSettings({ businessAddress: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Personal name</span>
          <input
            defaultValue={settings.personalName}
            onBlur={(e) => void saveSettings({ personalName: e.target.value })}
          />
        </label>
      </section>
    </>
  );
}

interface PanelProps {
  kind: LogoKind;
  title: string;
  storedPath: string | null;
  note: string;
  header: { name: string; lines: string[] };
}

function LogoPanel({ kind, title, storedPath, note, header }: PanelProps) {
  const [processed, setProcessed] = useState<ProcessedLogo | null>(null);
  const [storedUrl, setStoredUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void logoUrl(storedPath).then(setStoredUrl);
  }, [storedPath]);

  // Object URLs leak until revoked, and this screen can process many files in a session.
  useEffect(() => () => {
    if (processed) URL.revokeObjectURL(processed.url);
  }, [processed]);

  async function choose(file: File | undefined) {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const next = await processLogo(file, kind);
      setProcessed((previous) => {
        if (previous) URL.revokeObjectURL(previous.url);
        return next;
      });
    } catch (cause) {
      setError(cause instanceof LogoError ? cause.message : 'That image could not be processed.');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!processed) return;
    setBusy(true);
    try {
      await uploadLogo(kind, processed.blob);
      URL.revokeObjectURL(processed.url);
      setProcessed(null);
    } catch {
      setError('Upload failed. The change was not saved.');
    } finally {
      setBusy(false);
    }
  }

  const preview = processed?.url ?? storedUrl;

  return (
    <section className="panel">
      <h2>{title} logo</h2>
      <p className="muted small">{note}</p>

      {/* The sample header — checkerboard behind it, so a background that was NOT
          removed is visible rather than hiding against white. */}
      <div className="pdf-sample">
        <div className={`pdf-logo ${kind}`}>
          {preview ? (
            <img src={preview} alt={`${title} logo preview`} />
          ) : (
            <span className="muted small">No logo</span>
          )}
        </div>
        <div className="pdf-head">
          <strong>{header.name}</strong>
          {header.lines.map((line) => (
            <div key={line} className="muted small">
              {line}
            </div>
          ))}
        </div>
      </div>

      {processed && (
        <ul className="notes">
          {processed.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>
            Stored as PNG, {processed.width}×{processed.height}
          </li>
        </ul>
      )}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void choose(e.target.files?.[0])}
        />
        <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Working…' : 'Choose image'}
        </button>
        {processed && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            Use this logo
          </button>
        )}
      </div>
    </section>
  );
}
